<template>
  <fieldset>
    <SignupLegend title="Register your business"/>
    <div class="form-group row">
      <label class="col-sm-3 col-form-label">What is your company name?</label>
      <div class="col-sm-9">
        <BaseSearchCompany placeholder="Business Name" v-model="formData.name"
                           :isNameSearch="true"
                           :companySuggestions="companySuggestions"
                           :hasTag="formData.companiesHouseValidated"
                           @hide-dropdown="removeSuggestions"
                           @show-dropdown="searchCompany"
                           @select-item="selectCompany"
                           @remove-tag="removeCompany"
        />
      </div>
    </div>
    <div class="form-group row">
      <label class="col-sm-3 col-form-label">Or what is your company registration number?</label>
      <div class="col-sm-9">
        <BaseSearchCompany placeholder="Company Registration number" v-model="formData.registrationNo"
                           :isNameSearch="false"
                           :companySuggestions="companySuggestions"
                           :hasTag="formData.companiesHouseValidated"
                           @hide-dropdown="removeSuggestions"
                           @show-dropdown="searchCompany"
                           @select-item="selectCompany"
                           @remove-tag="removeCompany"
        />
        <div class="invalid-feedback">Company registration number is required.</div>
      </div>
    </div>
    <div class="form-group row" v-if="isCustom">
      <label class="col-sm-3 col-form-label">Type of business</label>
      <div class="col-sm-9">
        <BaseSelectOther :data="businessTypeOptions" v-model="formData.businessType" required>
          <span slot="invalid-feedback">Type of business is required.</span>
        </BaseSelectOther>
      </div>
    </div>
    <div class="form-group row">
      <label class="col-sm-3 col-form-label">Is your company regulated?</label>
      <div class="col-sm-9">
        <div class="form-check">
          <input type="radio" id="regulated1" class="form-check-input" name="regulated" :value="true"
                 v-model="formData.regulated"
                 required>
          <label class="form-check-label" for="regulated1">Yes</label>
        </div>
        <div class="form-check">
          <input type="radio" id="regulated2" class="form-check-input" name="regulated" :value="false"
                 v-model="formData.regulated"
                 required>
          <label class="form-check-label" for="regulated2">No</label>
          <div class="invalid-feedback">Please select either "Yes" or "No".</div>
        </div>
      </div>
    </div>
    <div class="form-group row" v-if="isCustom">
      <label class="col-sm-3 col-form-label">Date of incorporation</label>
      <div class="col-sm-9">
        <BaseSelectDate :maxYear="0" :minYear="150" v-model="formData.incorporationDate"/>
      </div>
    </div>
    <div class="row" v-show="youngCompany()">
      <p class="col warning"><span class="fas fa-exclamation-triangle"></span> If your company was incorporated less
        than a year ago, we might need some additional documentation like bank statements in order
        to perform a <a href="https://getaio.co.uk/kyb" target="_blank">KYB-check</a> on your company. We will
        contact you directly, if this is the case.
      </p>
    </div>
    <BaseNaviButtons :noBack="true" :toSubmit="formData"/>
  </fieldset>
</template>

<script>
  // TODO: add field for "admin contact", "preferred contact" / office manager (#63)
  // TODO: ask for company website
  // TODO: ask for office phone number

  import Address from '@/utils/Address'
  import BaseNaviButtons from '@/components/BaseNaviButtons'
  import BaseSearchCompany from '@/components/BaseSearchCompany'
  import BaseSelectDate from '@/components/BaseSelectDate'
  import BaseSelectOther from '@/components/BaseSelectOther'
  import Person from '@/utils/Person'
  import api from '@/utils/api'
  import SignupLegend from '@/components/SignupLegend'

  export default {
    components: {
      BaseNaviButtons,
      BaseSearchCompany,
      BaseSelectDate,
      BaseSelectOther,
      SignupLegend
    },
    computed: {
      isCustom () {
        return !this.formData.companiesHouseValidated && this.formData.name && this.formData.registrationNo
      }
    },
    data () {
      return {
        businessTypeOptions: [
          {value: 'ltd', displayValue: 'Private Limited Company (LTD)'},
          {value: 'plc', displayValue: 'Public Limited Company (PLC)'},
          {value: 'llp', displayValue: 'Limited Liability Partnership (LLP)'}
        ],
        companySuggestions: []
      }
    },
    methods: {
      youngCompany () {
        const age = (new Date() - this.formData.incorporationDate) / 31556952000
        return age < 1
      },
      removeCompany () {
        this.$emit('remove-company')
      },
      removeSuggestions () {
        this.companySuggestions = []
      },
      async searchCompany (isNameSearch) {
        const term = isNameSearch ? this.formData.name : this.formData.registrationNo
        if (term.length > 2) {
          const result = await api.searchCompanies(term)
          this.companySuggestions = result.items.slice(0, 8)
        } else this.removeSuggestions()
      },
      async selectCompany (company) {
        this.formData.name = company.title
        this.formData.registrationNo = company.company_number
        this.formData.companiesHouseValidated = true
        this.removeSuggestions()
        this.formData.businessType = company.company_type
        this.formData.incorporationDate = new Date(company.date_of_creation)
        this.formData.addressSuggestion = new Address(company.address)
        try {
          const officerResponse = await api.getCompanyOfficers(this.formData.registrationNo)
          const officers = officerResponse.items
          this.formData.activeOfficers = officers.filter(officer => !officer.resigned_on).map(o => new Person(o))
          const pcsResponse = await api.getCompanyPSCs(this.formData.registrationNo)
          const people = pcsResponse.items
          this.formData.chPSC = people.map(o => {
            const p = new Person(o)
            p.isShareholder = true
            return p
          })
        } catch (error) {
          if (error.response.status === 404) this.formData.chPSC = []
        }
      }
    },
    props: {
      formData: Object
    }
  }
</script>

<style lang="scss" scoped>
  @import '@/sass/variables.scss';

  .warning {
    color: $yellow;

    a {
      color: darken($yellow, 15%);
      text-decoration: underline;

      &:hover {
        color: darken($yellow, 30%);
      }
    }
  }
</style>
